<template>
  <div class="subprocessors-page">
    <!-- Header Navigation -->
    <header class="header">
      <nav class="navbar">
        <div class="nav-brand">
          <router-link to="/" class="brand-link">
            <img src="/favicon.svg" alt="ADI Logo" class="brand-icon">
            <span class="brand-logo">ADI</span>
            <span class="brand-text">Actionable Document Intelligence</span>
          </router-link>
        </div>
        <div class="nav-links">
          <router-link to="/" class="nav-link">Home</router-link>
          <router-link to="/login" class="btn btn-primary">Login</router-link>
        </div>
      </nav>
    </header>

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-content">
        <div class="legal-badge">
          <i class="fas fa-shield-alt"></i>
          Legal & Compliance
        </div>
        <h1 class="hero-title">Subprocessors</h1>
        <p class="hero-subtitle">
          Transparency about the third-party service providers we engage to deliver our services
        </p>
        <div class="last-updated">
          <i class="fas fa-clock"></i>
          Last updated: {{ lastUpdated }}
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <main class="content-section">
      <div class="container">
        <!-- Introduction -->
        <section class="intro-section">
          <div class="intro-card">
            <h2><i class="fas fa-info-circle"></i> About This Page</h2>
            <p>
              In accordance with our Data Processing Agreement (Section 5.5), this page maintains a current 
              list of subprocessors engaged by ADI (Actionable Document Intelligence) to provide our services. 
              We update this list within thirty (30) days of engaging any additional subprocessors.
            </p>
            <p>
              For healthcare and HIPAA-covered entities, we have indicated which providers have executed 
              Business Associate Agreements (BAAs) with us.
            </p>
          </div>
        </section>

        <!-- Cloud Infrastructure Section -->
        <section class="subprocessor-section">
          <div class="section-header">
            <div class="section-icon cloud">
              <i class="fas fa-cloud"></i>
            </div>
            <div class="section-title-group">
              <h2>Cloud Infrastructure & Storage</h2>
              <p>Core infrastructure services that power our platform</p>
            </div>
          </div>
          
          <div class="subprocessor-grid">
            <div class="subprocessor-card primary">
              <div class="card-header">
                <div class="provider-logo aws">AWS</div>
                <span class="baa-badge"><i class="fas fa-check-circle"></i> BAA Available</span>
              </div>
              <h3>Amazon Web Services, Inc.</h3>
              <p class="location"><i class="fas fa-map-marker-alt"></i> Seattle, WA, USA</p>
              <div class="services-list">
                <span class="service-tag">EC2 (Compute)</span>
                <span class="service-tag">S3 (Storage)</span>
                <span class="service-tag">RDS (Database)</span>
                <span class="service-tag">ElastiCache (Redis)</span>
                <span class="service-tag">SES (Email)</span>
                <span class="service-tag">Secrets Manager</span>
                <span class="service-tag">CloudWatch (Logging)</span>
                <span class="service-tag">ECS/Fargate (Containers)</span>
              </div>
              <div class="purpose">
                <strong>Purpose:</strong> Primary cloud infrastructure hosting all production services, 
                databases, document storage, and email delivery.
              </div>
              <div class="security-note">
                <i class="fas fa-lock"></i>
                All data encrypted at rest (AES-256) and in transit (TLS 1.2+)
              </div>
            </div>
          </div>
        </section>

        <!-- AI/LLM Providers Section -->
        <section class="subprocessor-section">
          <div class="section-header">
            <div class="section-icon ai">
              <i class="fas fa-brain"></i>
            </div>
            <div class="section-title-group">
              <h2>AI & Machine Learning Providers</h2>
              <p>Large Language Model providers for document analysis and intelligence</p>
            </div>
          </div>
          
          <div class="subprocessor-grid">
            <div class="subprocessor-card">
              <div class="card-header">
                <div class="provider-logo google">G</div>
                <span class="baa-badge"><i class="fas fa-check-circle"></i> BAA Available</span>
              </div>
              <h3>Google LLC (Gemini AI)</h3>
              <p class="location"><i class="fas fa-map-marker-alt"></i> Mountain View, CA, USA</p>
              <div class="services-list">
                <span class="service-tag">Gemini 2.5 Flash</span>
                <span class="service-tag">Gemini 2.5 Pro</span>
              </div>
              <div class="purpose">
                <strong>Purpose:</strong> Primary AI provider for document analysis, compliance checking, 
                and intelligent data extraction. Default provider for all new deployments.
              </div>
            </div>

            <div class="subprocessor-card">
              <div class="card-header">
                <div class="provider-logo openai">AI</div>
                <span class="baa-badge"><i class="fas fa-check-circle"></i> BAA Available</span>
              </div>
              <h3>OpenAI, LLC</h3>
              <p class="location"><i class="fas fa-map-marker-alt"></i> San Francisco, CA, USA</p>
              <div class="services-list">
                <span class="service-tag">GPT-4 Turbo</span>
                <span class="service-tag">GPT-4o</span>
                <span class="service-tag">GPT-3.5 Turbo</span>
              </div>
              <div class="purpose">
                <strong>Purpose:</strong> AI provider for document analysis, requirement generation, 
                and fallback processing when primary provider is unavailable.
              </div>
            </div>

            <div class="subprocessor-card">
              <div class="card-header">
                <div class="provider-logo anthropic">A</div>
                <span class="baa-badge"><i class="fas fa-check-circle"></i> BAA Available</span>
              </div>
              <h3>Anthropic, PBC</h3>
              <p class="location"><i class="fas fa-map-marker-alt"></i> San Francisco, CA, USA</p>
              <div class="services-list">
                <span class="service-tag">Claude 3 Haiku</span>
                <span class="service-tag">Claude 3 Sonnet</span>
                <span class="service-tag">Claude 3.5 Sonnet</span>
              </div>
              <div class="purpose">
                <strong>Purpose:</strong> Alternative AI provider for document analysis and 
                complex reasoning tasks.
              </div>
            </div>

            <div class="subprocessor-card no-baa">
              <div class="card-header">
                <div class="provider-logo xai">X</div>
                <span class="baa-badge warning"><i class="fas fa-exclamation-triangle"></i> No BAA</span>
              </div>
              <h3>xAI Corp (Grok)</h3>
              <p class="location"><i class="fas fa-map-marker-alt"></i> San Francisco, CA, USA</p>
              <div class="services-list">
                <span class="service-tag">Grok-2</span>
                <span class="service-tag">Grok-2 Vision</span>
              </div>
              <div class="purpose">
                <strong>Purpose:</strong> Alternative AI provider for document analysis.
              </div>
              <div class="healthcare-warning">
                <i class="fas fa-exclamation-circle"></i>
                <strong>Healthcare Notice:</strong> This provider is automatically disabled for 
                healthcare/HIPAA deployments as no Business Associate Agreement is available.
              </div>
            </div>
          </div>
        </section>

        <!-- Payment Processing Section -->
        <section class="subprocessor-section">
          <div class="section-header">
            <div class="section-icon payment">
              <i class="fas fa-credit-card"></i>
            </div>
            <div class="section-title-group">
              <h2>Payment Processing</h2>
              <p>Secure payment and subscription management services</p>
            </div>
          </div>
          
          <div class="subprocessor-grid">
            <div class="subprocessor-card">
              <div class="card-header">
                <div class="provider-logo stripe">S</div>
                <span class="pci-badge"><i class="fas fa-shield-alt"></i> PCI DSS Compliant</span>
              </div>
              <h3>Stripe, Inc.</h3>
              <p class="location"><i class="fas fa-map-marker-alt"></i> San Francisco, CA, USA</p>
              <div class="services-list">
                <span class="service-tag">Payment Processing</span>
                <span class="service-tag">Subscription Billing</span>
                <span class="service-tag">Stripe Connect</span>
                <span class="service-tag">Customer Portal</span>
              </div>
              <div class="purpose">
                <strong>Purpose:</strong> Handles all payment processing, subscription management, 
                and marketplace transactions. No payment card data is stored on our systems.
              </div>
            </div>
          </div>
        </section>

        <!-- Security Section -->
        <section class="subprocessor-section">
          <div class="section-header">
            <div class="section-icon security">
              <i class="fas fa-shield-alt"></i>
            </div>
            <div class="section-title-group">
              <h2>Security Services</h2>
              <p>Services that enhance platform security and prevent abuse</p>
            </div>
          </div>
          
          <div class="subprocessor-grid">
            <div class="subprocessor-card">
              <div class="card-header">
                <div class="provider-logo google">G</div>
              </div>
              <h3>Google LLC (reCAPTCHA)</h3>
              <p class="location"><i class="fas fa-map-marker-alt"></i> Mountain View, CA, USA</p>
              <div class="services-list">
                <span class="service-tag">reCAPTCHA v2</span>
                <span class="service-tag">Bot Protection</span>
              </div>
              <div class="purpose">
                <strong>Purpose:</strong> Protects login and registration forms from automated 
                attacks and bot abuse.
              </div>
            </div>
          </div>
        </section>

        <!-- Video Hosting Section -->
        <section class="subprocessor-section">
          <div class="section-header">
            <div class="section-icon video">
              <i class="fas fa-video"></i>
            </div>
            <div class="section-title-group">
              <h2>Video Hosting (LMS)</h2>
              <p>Video hosting services for our Learning Management System</p>
            </div>
          </div>
          
          <div class="subprocessor-grid">
            <div class="subprocessor-card">
              <div class="card-header">
                <div class="provider-logo vimeo">V</div>
              </div>
              <h3>Vimeo, Inc.</h3>
              <p class="location"><i class="fas fa-map-marker-alt"></i> New York, NY, USA</p>
              <div class="services-list">
                <span class="service-tag">Video Hosting</span>
                <span class="service-tag">Video Streaming</span>
                <span class="service-tag">Video Analytics</span>
              </div>
              <div class="purpose">
                <strong>Purpose:</strong> Hosts and streams educational video content for 
                our Learning Management System (LMS) courses.
              </div>
            </div>
          </div>
        </section>

        <!-- Data Processing Notice -->
        <section class="notice-section">
          <div class="notice-card">
            <h3><i class="fas fa-gavel"></i> Data Processing Commitment</h3>
            <ul>
              <li>
                <i class="fas fa-check"></i>
                ADI remains responsible for all subprocessors' compliance with HIPAA and our 
                Data Processing Agreement obligations.
              </li>
              <li>
                <i class="fas fa-check"></i>
                We conduct due diligence on all subprocessors to ensure they meet our security 
                and privacy standards.
              </li>
              <li>
                <i class="fas fa-check"></i>
                Healthcare deployments automatically disable any AI providers without BAA capability.
              </li>
              <li>
                <i class="fas fa-check"></i>
                This list is updated within 30 days of engaging any new subprocessor.
              </li>
            </ul>
          </div>
        </section>

        <!-- Contact Section -->
        <section class="contact-section">
          <div class="contact-card">
            <h3><i class="fas fa-envelope"></i> Questions?</h3>
            <p>
              If you have questions about our subprocessors or data processing practices, 
              please contact our Data Protection team:
            </p>
            <a href="mailto:privacy@get-adi.com" class="contact-email">
              <i class="fas fa-envelope"></i> privacy@get-adi.com
            </a>
          </div>
        </section>
      </div>
    </main>

    <!-- Footer -->
    <footer class="footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-brand">
            <img src="/favicon.svg" alt="ADI Logo" class="brand-icon">
            <span class="brand-logo">ADI</span>
          </div>
          <div class="footer-links">
            <router-link to="/">Home</router-link>
            <router-link to="/subprocessors" class="active">Subprocessors</router-link>
            <a href="mailto:privacy@get-adi.com">Contact</a>
          </div>
        </div>
        <div class="footer-bottom">
          <p>&copy; {{ currentYear }} ADI - Actionable Document Intelligence. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'SubprocessorsPage',
  
  data() {
    return {
      lastUpdated: 'December 18, 2025'
    }
  },
  
  computed: {
    currentYear() {
      return new Date().getFullYear()
    }
  }
}
</script>

<style scoped>
.subprocessors-page {
  margin: -2rem;
  width: calc(100% + 4rem);
  background: linear-gradient(135deg, #0f1419 0%, #1a252f 100%);
  min-height: 100vh;
  color: #e7e9ea;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* Header */
.header {
  background: rgba(15, 20, 25, 0.95);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(10px);
}

.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.nav-brand {
  display: flex;
  align-items: center;
}

.brand-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
}

.brand-icon {
  width: 32px;
  height: 32px;
}

.brand-logo {
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #00a67e 0%, #00d4aa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.brand-text {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 400;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.nav-link {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.nav-link:hover {
  color: #00a67e;
}

.btn {
  display: inline-block;
  padding: 0.6rem 1.25rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
}

.btn-primary {
  background: linear-gradient(135deg, #00a67e 0%, #00d4aa 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 166, 126, 0.4);
}

/* Hero Section */
.hero-section {
  padding: 4rem 2rem 3rem;
  text-align: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.hero-content {
  max-width: 700px;
  margin: 0 auto;
}

.legal-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(0, 166, 126, 0.15);
  color: #00d4aa;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(0, 166, 126, 0.3);
}

.hero-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ffffff 0%, #a8b3cf 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.last-updated {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.85rem;
}

/* Content Section */
.content-section {
  padding: 3rem 2rem;
}

.container {
  max-width: 1100px;
  margin: 0 auto;
}

/* Intro Section */
.intro-section {
  margin-bottom: 3rem;
}

.intro-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 2rem;
}

.intro-card h2 {
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: #e7e9ea;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.intro-card h2 i {
  color: #00a67e;
}

.intro-card p {
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.7;
  margin-bottom: 1rem;
}

.intro-card p:last-child {
  margin-bottom: 0;
}

/* Subprocessor Sections */
.subprocessor-section {
  margin-bottom: 3rem;
}

.section-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.section-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.section-icon.cloud {
  background: linear-gradient(135deg, #ff9500 0%, #ff6b00 100%);
}

.section-icon.ai {
  background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
}

.section-icon.payment {
  background: linear-gradient(135deg, #635bff 0%, #7c3aed 100%);
}

.section-icon.security {
  background: linear-gradient(135deg, #00a67e 0%, #00d4aa 100%);
}

.section-icon.video {
  background: linear-gradient(135deg, #1ab7ea 0%, #0ea5e9 100%);
}

.section-title-group h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: #e7e9ea;
}

.section-title-group p {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
}

/* Subprocessor Grid */
.subprocessor-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}

.subprocessor-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.3s ease;
}

.subprocessor-card:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
}

.subprocessor-card.primary {
  grid-column: 1 / -1;
  background: linear-gradient(135deg, rgba(255, 149, 0, 0.08) 0%, rgba(255, 107, 0, 0.05) 100%);
  border-color: rgba(255, 149, 0, 0.2);
}

.subprocessor-card.no-baa {
  border-color: rgba(245, 158, 11, 0.3);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.provider-logo {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  color: white;
}

.provider-logo.aws {
  background: linear-gradient(135deg, #ff9500 0%, #ff6b00 100%);
  font-size: 0.7rem;
}

.provider-logo.google {
  background: linear-gradient(135deg, #4285f4 0%, #1a73e8 100%);
}

.provider-logo.openai {
  background: linear-gradient(135deg, #10a37f 0%, #0d8a6a 100%);
}

.provider-logo.anthropic {
  background: linear-gradient(135deg, #cc785c 0%, #b5634a 100%);
}

.provider-logo.xai {
  background: linear-gradient(135deg, #1d9bf0 0%, #0c7abf 100%);
}

.provider-logo.stripe {
  background: linear-gradient(135deg, #635bff 0%, #5046e5 100%);
}

.provider-logo.vimeo {
  background: linear-gradient(135deg, #1ab7ea 0%, #0ea5e9 100%);
}

.baa-badge, .pci-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: rgba(0, 166, 126, 0.15);
  color: #00d4aa;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
}

.baa-badge.warning {
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
}

.subprocessor-card h3 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #e7e9ea;
}

.location {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.85rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.services-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.service-tag {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.8);
  padding: 0.25rem 0.6rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.purpose {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.purpose strong {
  color: rgba(255, 255, 255, 0.8);
}

.security-note {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(0, 166, 126, 0.1);
  color: #00d4aa;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.8rem;
}

.healthcare-warning {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.8rem;
  line-height: 1.5;
}

.healthcare-warning i {
  margin-right: 0.35rem;
}

/* Notice Section */
.notice-section {
  margin-top: 3rem;
  margin-bottom: 2rem;
}

.notice-card {
  background: rgba(0, 166, 126, 0.05);
  border: 1px solid rgba(0, 166, 126, 0.2);
  border-radius: 16px;
  padding: 2rem;
}

.notice-card h3 {
  font-size: 1.25rem;
  margin-bottom: 1.25rem;
  color: #e7e9ea;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.notice-card h3 i {
  color: #00a67e;
}

.notice-card ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.notice-card li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.75rem;
  line-height: 1.6;
}

.notice-card li:last-child {
  margin-bottom: 0;
}

.notice-card li i {
  color: #00d4aa;
  margin-top: 0.25rem;
  flex-shrink: 0;
}

/* Contact Section */
.contact-section {
  margin-top: 2rem;
}

.contact-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
}

.contact-card h3 {
  font-size: 1.25rem;
  margin-bottom: 0.75rem;
  color: #e7e9ea;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.contact-card h3 i {
  color: #00a67e;
}

.contact-card p {
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 1.25rem;
}

.contact-email {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #00a67e 0%, #00d4aa 100%);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
}

.contact-email:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 166, 126, 0.4);
}

/* Footer */
.footer {
  background: rgba(0, 0, 0, 0.3);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  padding: 2rem;
  margin-top: 3rem;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.footer-brand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.footer-brand .brand-icon {
  width: 28px;
  height: 28px;
}

.footer-brand .brand-logo {
  font-size: 1.25rem;
}

.footer-links {
  display: flex;
  gap: 2rem;
}

.footer-links a {
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.2s;
}

.footer-links a:hover,
.footer-links a.active {
  color: #00a67e;
}

.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 1.5rem;
  text-align: center;
}

.footer-bottom p {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.85rem;
  margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .navbar {
    flex-direction: column;
    gap: 1rem;
  }
  
  .hero-title {
    font-size: 2rem;
  }
  
  .section-header {
    flex-direction: column;
    text-align: center;
    align-items: center;
  }
  
  .subprocessor-grid {
    grid-template-columns: 1fr;
  }
  
  .subprocessor-card.primary {
    grid-column: 1;
  }
  
  .footer-content {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
  }
  
  .footer-links {
    flex-wrap: wrap;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .hero-section {
    padding: 3rem 1rem 2rem;
  }
  
  .content-section {
    padding: 2rem 1rem;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .nav-links {
    gap: 1rem;
  }
}
</style>




